import callApi from "../../../utils/api";
import { UseQueryResult, useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "../../../utils/providers/query-client-provider";
import {
  NotificationInterfaceType,
  NotificationType,
} from "../notifications-type";
import { Session } from "next-auth";
import { useEffect } from "react";

export async function getNotifications(portal: "seller" | "customer") {
  return callApi<NotificationInterfaceType>({
    path: `/webnotification/${portal}/notifications?pageSize=100`,
    method: "GET",
  });
}

async function deleteNotification({
  id,
  portal,
}: {
  id: string;
  portal: "seller" | "customer";
}) {
  return await callApi({
    path: `/webnotification/${portal}/notifications/delete/${id}`,
    method: "DELETE",
  });
}

export async function markNotificationAsRead({
  id,
  portal,
}: {
  id: string;
  portal: "seller" | "customer";
}) {
  return await callApi({
    path: `/webnotification/${portal}/notifications/mark/${id}`,
    method: "PUT",
  });
}

function useNotificationsBase(
  select: any,
  portal: "seller" | "customer",
): UseQueryResult<any, Error> {
  return useQuery({
    queryKey: ["notifications"],
    queryFn: () => getNotifications(portal),
    select,
    staleTime: 60000 * 60,
  });
}

export function useNotifications(
  portal: "seller" | "customer",
): UseQueryResult<NotificationInterfaceType, Error> {
  return useNotificationsBase(
    (notifications: NotificationInterfaceType) => notifications,
    portal,
  );
}

export function useNotificationsCounter(
  portal: "seller" | "customer",
): UseQueryResult<number, Error> {
  return useNotificationsBase(
    (notifications: NotificationInterfaceType): number =>
      notifications.list.filter(
        (notification: NotificationType) => !notification.read,
      ).length,
    portal,
  );
}

export function useDeleteNotification(): any {
  return useMutation({
    //mutationKey: ["notifications"],
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export function useMarkNotificationAsRead(): any {
  return useMutation({
    mutationFn: markNotificationAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
}

export const useNotificationsSubscription = (
  session: Session | null,
  portal: "seller" | "customer",
) => {
  let websocket: EventSource | null = null;
  let retryTimer: number | null = null;
  useEffect(() => {
    if (session && !(websocket?.readyState === 1)) {
      const connect = () => {
        websocket = new EventSource(
          `/webnotification/${portal}/notifications/stream`,
        );

        websocket.onopen = () => {
          console.info("* Notifications events bus opened *");
          if (retryTimer) {
            clearInterval(retryTimer);
            retryTimer = null;
          }
        };

        websocket.onerror = (e) => {
          console.info("* Notifications events bus error *");
          console.error(e);
          websocket?.close();

          if (!retryTimer) {
            console.info("* Retrying notifications connection in 1 min *");
            retryTimer = window.setInterval(() => {
              console.info("* Retrying notifications connection... *");
              connect();
            }, 60000 * 5);
          }
        };

        websocket.onmessage = (e) => {
          const data = JSON.parse(e.data);
          const notification = JSON.parse(data);

          queryClient.setQueriesData(
            { queryKey: ["notifications"] },
            (oldData: NotificationInterfaceType) => {
              if (oldData) {
                if (
                  oldData.list.find((item: NotificationType) => {
                    return item.id === notification.id;
                  })
                ) {
                  return oldData;
                }
                return {
                  ...oldData,
                  list: [notification, ...oldData.list],
                  totalItems: oldData?.totalItems + 1,
                };
              }

              return {
                list: [notification],
                totalItems: 1,
                totalPages: 1,
              };
            },
          );
        };
      };

      connect();

      return () => {
        console.log("* Notifications events bus closed");
        websocket?.close();
        if (retryTimer) clearInterval(retryTimer);
      };
    }
  }, [queryClient, session]);
};
